// Collecting what the bot should know.
//
// Four kinds of source, three destinations inside Google (see ARCHITECTURE.md
// section 3), but the customer should not have to care about that: they have
// FAQs, a document, a website, and a paragraph about the business. So the UI is
// four buttons and the mapping happens server-side.
//
// Files are the odd one out. They are PUT straight to Cloud Storage on a signed
// URL *before* the agent is submitted, so what ends up in `sources` is a
// storagePath rather than bytes -- which also means an upload that fails is a
// visible per-row error rather than a form submission that silently drops a file.

import { useRef, useState } from "react";
import * as api from "../lib/api";
import { FILE_ACCEPT, LIMITS, fileExtension, fileTooBigMessage, maxFileBytes, validateSource } from "../lib/knowledge";
import { formatBytes } from "../lib/format";
import type { KnowledgeSource } from "../lib/types";
import { Button, Field, TextArea, TextInput } from "./ui";
import { SOURCE_LABELS, SourceIcon } from "./SourceIcon";
import WrittenWinsNote from "./WrittenWinsNote";

type Props = {
  // null before the visitor has an account. Everything except file upload works
  // without one: Q&A, a URL and pasted text are just strings until the agent is
  // submitted, whereas a file has to go somewhere, and there is no tenant to put
  // it under yet. So uploading is the one thing that asks them to sign in first.
  tenantId: string | null;
  // Absent while the agent is still being created in the wizard; the signing
  // endpoint accepts that and files land under an "unassigned" path.
  agentId?: string;
  sources: KnowledgeSource[];
  onChange: (sources: KnowledgeSource[]) => void;
  // The bot already knows something (the Knowledge tab's Add more), so the
  // empty box invites more rather than asking for a first source.
  hasExisting?: boolean;
};

export default function KnowledgeEditor({ tenantId, agentId, sources, onChange, hasExisting = false }: Props) {
  const canUpload = tenantId !== null;
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  function update(index: number, next: KnowledgeSource) {
    onChange(sources.map((source, i) => (i === index ? next : source)));
  }

  function remove(index: number) {
    onChange(sources.filter((_, i) => i !== index));
  }

  function add(source: KnowledgeSource) {
    if (sources.length >= LIMITS.maxSourcesPerAgent) return;
    onChange([...sources, source]);
  }

  async function onFilesPicked(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (!tenantId) return;
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded: KnowledgeSource[] = [];
      for (const file of Array.from(files)) {
        // Checked here as well as server-side so a file that is too big is
        // refused instantly rather than after it has been read off disk.
        const ext = fileExtension(file.name);
        if (ext && file.size > maxFileBytes(ext)) {
          throw new Error(fileTooBigMessage({ fileName: file.name, ext, sizeBytes: file.size }));
        }
        const result = await api.uploadKnowledgeFile(tenantId, agentId, file);
        uploaded.push({ type: "file", ...result });
      }
      onChange([...sources, ...uploaded]);
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
      // Cleared so picking the same file twice in a row still fires a change.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  const urlCount = sources.filter((source) => source.type === "url").length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={() => add({ type: "qa", pairs: [{ question: "", answer: "" }] })}>
          <SourceIcon type="qa" />
          Q&amp;A pairs
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => add({ type: "url", url: "" })}
          disabled={urlCount >= LIMITS.maxUrlsPerAgent}
        >
          <SourceIcon type="url" />
          Website page
        </Button>
        <Button type="button" variant="secondary" onClick={() => add({ type: "text", text: "" })}>
          <SourceIcon type="text" />
          Freeform text
        </Button>
        <Button
          type="button"
          variant="secondary"
          busy={uploading}
          disabled={!canUpload}
          title={canUpload ? undefined : "Create your account to upload documents"}
          onClick={() => fileInput.current?.click()}
        >
          {!uploading && <SourceIcon type="file" />}
          Upload documents
        </Button>
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={FILE_ACCEPT}
          className="hidden"
          onChange={(event) => void onFilesPicked(event.target.files)}
        />
      </div>

      {!canUpload && (
        <p className="text-sm text-slate-500">
          Documents need an account to upload to. Everything else here works without one, and you
          can add files once you have signed up.
        </p>
      )}

      {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

      {sources.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          {hasExisting ? (
            <>Choose what to add with the buttons above. What the bot already knows stays as it is.</>
          ) : (
            <>
              Add at least one source. Most people start by pasting their FAQ as Q&amp;A pairs and
              adding their website.
            </>
          )}
        </p>
      )}

      <ul className="space-y-3">
        {sources.map((source, index) => {
          const check = validateSource(source);
          return (
            <li key={index} className="rounded-lg border border-slate-200 bg-surface p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <SourceIcon type={source.type} className="h-4 w-4 text-indigo-600" />
                  {SOURCE_LABELS[source.type]}
                </span>
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-red-600"
                  onClick={() => remove(index)}
                >
                  Remove
                </button>
              </div>

              {source.type === "url" && (
                <Field label="Page or site to index" hint="We read this page and the pages under it, up to 100 pages. FAQ, shipping and policy pages come first.">
                  <TextInput
                    type="url"
                    inputMode="url"
                    placeholder="https://example.com/help"
                    value={source.url}
                    onChange={(event) => update(index, { type: "url", url: event.target.value })}
                  />
                </Field>
              )}

              {source.type === "text" && (
                <>
                  <WrittenWinsNote className="mb-3" />
                  <Field label="Anything the bot should know" hint={`Up to ${LIMITS.textChars.toLocaleString()} characters.`}>
                    <TextArea
                      rows={5}
                      placeholder="We're open 9-5 Monday to Friday, we ship to the US and Canada, and returns are free within 30 days."
                      value={source.text}
                      onChange={(event) => update(index, { type: "text", text: event.target.value })}
                    />
                  </Field>
                </>
              )}

              {source.type === "qa" && (
                <>
                  <WrittenWinsNote className="mb-3" />
                  <QaEditor pairs={source.pairs} onChange={(pairs) => update(index, { type: "qa", pairs })} />
                </>
              )}

              {source.type === "file" && (
                <p className="text-sm text-slate-700">
                  {source.fileName}{" "}
                  <span className="text-xs text-slate-500">({formatBytes(source.sizeBytes)}, uploaded)</span>
                </p>
              )}

              {/* Only shown once the row has something in it: telling someone
                  their brand-new empty field is empty is noise, not help. */}
              {!check.ok && !isBlank(source) && (
                <p className="mt-2 text-xs text-red-600">{check.error}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function isBlank(source: KnowledgeSource): boolean {
  if (source.type === "url") return !source.url.trim();
  if (source.type === "text") return !source.text.trim();
  if (source.type === "qa") return source.pairs.every((p) => !p.question.trim() && !p.answer.trim());
  return false;
}

function QaEditor({
  pairs,
  onChange
}: {
  pairs: { question: string; answer: string }[];
  onChange: (pairs: { question: string; answer: string }[]) => void;
}) {
  return (
    <div className="space-y-3">
      {pairs.map((pair, index) => (
        <div key={index} className="space-y-2 rounded-md bg-slate-50 p-3">
          <TextInput
            placeholder="Do you offer refunds?"
            value={pair.question}
            maxLength={LIMITS.qaQuestionChars}
            onChange={(event) =>
              onChange(pairs.map((p, i) => (i === index ? { ...p, question: event.target.value } : p)))
            }
          />
          <TextArea
            rows={3}
            placeholder="Yes, within 30 days of delivery, no questions asked."
            value={pair.answer}
            maxLength={LIMITS.qaAnswerChars}
            onChange={(event) =>
              onChange(pairs.map((p, i) => (i === index ? { ...p, answer: event.target.value } : p)))
            }
          />
          {pairs.length > 1 && (
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-red-600"
              onClick={() => onChange(pairs.filter((_, i) => i !== index))}
            >
              Remove this pair
            </button>
          )}
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        onClick={() => onChange([...pairs, { question: "", answer: "" }])}
      >
        + Add another pair
      </Button>
    </div>
  );
}
